/**
 * Generic Fetch State Types (Discriminated Union)
 * Reusable across any fetch operation in the application
 *
 * The discriminated union ensures type safety by preventing access to `data`
 * when the status is 'error' or 'idle', and preventing access to `error`
 * when the status is 'success'.
 *
 * The 'loading' status can now preserve previous successful data (T | null)
 * to support background refreshes without flickering.
 */

export type FetchState<T> =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: T | null; error: null }
  | { status: 'success'; data: T; error: null }
  | { status: 'error'; data: null; error: Error };

/**
 * Extract the status type from FetchState
 */
export type FetchStatus = FetchState<unknown>['status'];

/**
 * Type guard to check if fetch is in success state
 * Narrows the type to allow safe access to `data`
 *
 * @param state - The fetch state to check
 * @returns true if status is 'success'
 */
export const isFetchSuccess = <T>(
  state: FetchState<T>
): state is Extract<FetchState<T>, { status: 'success' }> => {
  return state.status === 'success';
};

/**
 * Type guard to check if fetch is in error state
 * Narrows the type to allow safe access to `error`
 *
 * @param state - The fetch state to check
 * @returns true if status is 'error'
 */
export const isFetchError = <T>(
  state: FetchState<T>
): state is Extract<FetchState<T>, { status: 'error' }> => {
  return state.status === 'error';
};
